import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Fragment, useState } from 'react'
import { driverErrorsTableQuery, type DriverErrorRow } from '../api/devices'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import type { ScreenProps } from './screens'

/**
 * Fila Remote Driver Errors - `Opteemum::remoteDriverErrors`
 * (`opteemum/devices/details/driver_issues.php`): tabelul `DriverIssuesModel`
 * din opteemum.js (Details / Name / Hardware Id / Date, sortat pe data,
 * descrescator) si randul desfacut cu textul erorii
 * (`Proactivitee::driverIssueDetail`: `<i class="fas fa-info-circle"></i> eroarea`).
 */

const COLUMNS: Array<{ title: string; orderable: boolean }> = [
  { title: '', orderable: false },
  { title: 'Name', orderable: true },
  { title: 'Hardware Id', orderable: true },
  { title: 'Date', orderable: true },
]

export function DeviceDriverErrors({ id }: ScreenProps) {
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(10)
  const [search, setSearch] = useState('')
  const [order, setOrder] = useState<{ column: number; dir: 'asc' | 'desc' }>({ column: 3, dir: 'desc' })
  const [open, setOpen] = useState<Set<number>>(() => new Set())

  const table = useQuery({
    ...driverErrorsTableQuery(id, { start, length, search, orderColumn: order.column, orderDir: order.dir }),
    placeholderData: keepPreviousData,
  })
  const rows: DriverErrorRow[] = table.data?.data ?? []
  const total = table.data?.recordsFiltered ?? 0
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const tid = 'driverIssuesTable'

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${tid}_wrapper`}>
          <div className="row">
            <div className="col-sm-6 dataTableF">
              <div className="dataTables_filter" id={`${tid}_filter`}>
                <label>
                  Search:{' '}
                  <input
                    type="search"
                    className="form-control input-sm"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value)
                      setStart(0)
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
          <table className="table table-striped table-hover dataTable driverIssuesTable no-footer" width="100%" id={tid} role="grid">
            <thead>
              <tr role="row">
                {COLUMNS.map((c, i) => (
                  <th
                    key={i}
                    className={c.orderable ? (order.column === i ? `sorting_${order.dir}` : 'sorting') : 'sorting_disabled'}
                    onClick={() => {
                      if (!c.orderable) return
                      setOrder({ column: i, dir: order.column === i && order.dir === 'asc' ? 'desc' : 'asc' })
                    }}
                  >
                    {c.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.isError ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="dataTables_empty">
                    {table.error.message}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="dataTables_empty">
                    {table.isPending ? 'Loading...' : 'No data available in table'}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const shown = open.has(row.ID)
                  return (
                    <Fragment key={row.DT_RowId}>
                      <tr id={row.DT_RowId} role="row" className={shown ? 'details' : undefined}>
                        <td>
                          <center
                            className="expandDetails color-blue pointer font-12"
                            data-type="problems"
                            data-id={row.ID}
                            onClick={() =>
                              setOpen((s) => {
                                const next = new Set(s)
                                if (next.has(row.ID)) next.delete(row.ID)
                                else next.add(row.ID)
                                return next
                              })
                            }
                          >
                            <i className={shown ? 'fa fa-angle-double-down' : 'fa fa-angle-double-right'}></i> Details
                          </center>
                        </td>
                        <td>{row.name}</td>
                        <td>{row.hardware_id}</td>
                        <td>{row.date}</td>
                      </tr>
                      {shown ? (
                        <tr className="rowDetails">
                          <td colSpan={COLUMNS.length}>
                            <i className="fas fa-info-circle"></i> {row.error}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })
              )}
            </tbody>
          </table>
          <div className="row">
            <div className="col-sm-2">
              <div className="dataTables_length custom_length_class" id={`${tid}_length`}>
                <label>
                  Show{' '}
                  <select
                    className="form-control input-sm"
                    value={length}
                    onChange={(e) => {
                      setLength(Number(e.target.value))
                      setStart(0)
                    }}
                  >
                    {LENGTHS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>{' '}
                  entries
                </label>
              </div>
            </div>
            <div className="col-sm-4 aright">
              <div className="dataTables_info" id={`${tid}_info`} role="status" aria-live="polite">
                Showing {total === 0 ? 0 : start + 1} to {Math.min(start + length, total)} of {total} entries
              </div>
            </div>
            <div className="col-sm-6">
              <div className="dataTables_paginate paging_simple_numbers" id={`${tid}_paginate`}>
                <ul className="pagination">
                  <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (page > 0) setStart((page - 1) * length)
                      }}
                    >
                      Previous
                    </a>
                  </li>
                  {pageButtons(page, pages).map((b, i) =>
                    b === 'ellipsis' ? (
                      <li key={`e${i}`} className="paginate_button disabled">
                        <a href="#" onClick={(e) => e.preventDefault()}>
                          …
                        </a>
                      </li>
                    ) : (
                      <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault()
                            setStart(b * length)
                          }}
                        >
                          {b + 1}
                        </a>
                      </li>
                    ),
                  )}
                  <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (page < pages - 1) setStart((page + 1) * length)
                      }}
                    >
                      Next
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
